import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import React from 'react';

import { MultiSelectInputV2 } from '../multi-select-input-v2/multi-select-input-v2.component';
import useMultiObjectSelectInput from './multi-object-select-input.controller';

export interface MultiUrlParams<T> {
    param: string;
    fetch: (id: string[]) => Promise<T[]>;
}

export interface MultiObjectSelectInputProps<T extends { id: string }, D extends string | number> {
    id?: string;
    label: React.ReactNode;
    helpMessage?: React.ReactNode;
    className?: string;

    required?: boolean;
    disabled?: boolean;
    hideOnOneOption?: boolean;
    hidden?: boolean;
    isClearable?: boolean;

    /**
     * @description Will check the current URL for a path param that matches this value.
     * If found (using the fetch function) the select will be disabled and the value will be set.
     * Otherwise the select will function as usual.
     * Do not use with useQueryParam (may cause unexpected behaviour)
     */
    useUrlOverwrite?: MultiUrlParams<T>;
    /**
     * @description Will check the current URL for a query param that matches this value.
     * If found (using the fetch function) the value will be set.
     * When selecting a new value, the URL will be updated.
     * Do not use with useUrlOverwrite (may cause unexpected behaviour)
     */
    useQueryParam?: MultiUrlParams<T>;
    onChange?: (value: T[]) => void;
    onChangeObject?: (value: T[]) => void;
    idValues?: string[];
    value: string[];
    searchValueOverwrite?: string;
    queryParams?: ApiQueryParams<D>;
    fetch: (queryParams?: ApiQueryParams<DefaultQueryParams | D>) => Promise<PaginatedResponse<T>>;
    multiFetch?: (ids: string[]) => Promise<T[]>;
    itemLabel: (item: T) => string;
    disableFetch?: boolean;
    mapOptions?: keyof T;
    subset?: string[];
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    isOpenOverwrite?: boolean;
    onScrollToBottom?: () => void;
}

export const MultiObjectSelectInput = <T extends { id: string }, D extends string | number>(props: MultiObjectSelectInputProps<T, D>) => {
    const viewProps = useMultiObjectSelectInput(props);
    if (viewProps.hide) return <></>;

    return (
        <MultiSelectInputV2
            {...props}
            id={props.id}
            label={props.label}
            className={props.className}
            options={viewProps.options}
            onSelect={viewProps.onSelect}
            value={viewProps.value}
            isClearable={props.isClearable}
            disabled={props.disabled}
            onScrollToBottom={viewProps.onScrollToBottom}
            onSearch={viewProps.search}
        />
    );
};
